import { ArrowDown, ArrowUp, ChevronRight, ChevronsUpDown } from 'lucide-react'
import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import type { ProductBadge } from '../segments'
import type { RealtimeProduct, RealtimeProductSortKey } from '../realtime'
import type { ProductRecord } from '../types'
import { integer, money, percent } from '../utils'

interface RealtimeProductAnalysisTableProps {
  products: RealtimeProduct[]
  productRecords: Map<string, ProductRecord>
  sortKey: RealtimeProductSortKey
  sortDirection: 'asc' | 'desc'
  onSort: (key: RealtimeProductSortKey) => void
  showAverageItemPrice?: boolean
  compact?: boolean
  badgesByProductId?: Map<string, ProductBadge[]>
  emptyMessage?: string
  erpAvailableByMerchantSku?: ReadonlyMap<string, number>
}

const columns: Array<{
  key: RealtimeProductSortKey
  label: string
  skuOnly?: boolean
}> = [
  { key: 'gmv', label: '实时成交金额' },
  { key: 'soldItemCount', label: '成交件数' },
  { key: 'averageItemPrice', label: '件单价', skuOnly: true },
  { key: 'productVisitors', label: '商品访客' },
  { key: 'productPageviews', label: '浏览量' },
  { key: 'conversionRate', label: '转化率' },
  { key: 'averageOrderValue', label: '客单价' },
  { key: 'delta', label: '较上次' },
]

function SortIcon({ active, direction }: { active: boolean, direction: 'asc' | 'desc' }) {
  if (!active) return <ChevronsUpDown aria-hidden="true" />
  return direction === 'asc' ? <ArrowUp aria-hidden="true" /> : <ArrowDown aria-hidden="true" />
}

function DeltaValue({ product }: { product: RealtimeProduct }) {
  if (!product.previousMetrics) return <span className="delta-neutral">—</span>
  const delta = product.metrics.gmv - product.previousMetrics.gmv
  if (!delta) return <span className="delta-neutral">—</span>
  return (
    <span className={delta > 0 ? 'delta-positive' : 'delta-negative'}>
      {delta > 0 ? <ArrowUp aria-hidden="true" /> : <ArrowDown aria-hidden="true" />}
      {delta > 0 ? '+' : '−'}{money(Math.abs(delta))}
    </span>
  )
}

export function RealtimeProductAnalysisTable({
  products,
  productRecords,
  sortKey,
  sortDirection,
  onSort,
  showAverageItemPrice = false,
  compact = false,
  badgesByProductId,
  emptyMessage = '没有符合当前筛选条件的实时商品。',
  erpAvailableByMerchantSku,
}: RealtimeProductAnalysisTableProps) {
  const visibleColumns = columns.filter((column) => showAverageItemPrice || !column.skuOnly)

  return (
    <div className="table-responsive realtime-analysis-table-wrap">
      <table className={`product-table realtime-product-analysis-table${compact ? ' is-compact' : ''}`}>
        <thead>
          <tr>
            <th className="product-column">商品</th>
            {visibleColumns.map((column) => (
              <Fragment key={column.key}>
                <th>
                  <button type="button" className="sort-button" onClick={() => onSort(column.key)}>
                    {column.label}
                    <SortIcon active={sortKey === column.key} direction={sortDirection} />
                  </button>
                </th>
                {erpAvailableByMerchantSku && column.key === 'soldItemCount' && <th>ERP库存</th>}
              </Fragment>
            ))}
            <th aria-label="查看详情" />
          </tr>
        </thead>
        <tbody>
          {products.map((product) => {
            const record = productRecords.get(`${product.level}:${product.id}`)
            const displayName = record?.nameNote || product.name
            const displayImage = product.image || record?.image
            const merchantSku = product.merchantSku || record?.merchantSku
            const erpAvailable = erpAvailableByMerchantSku?.get(merchantSku?.trim() || '')
            const detailUrl = `/product/${product.level}/${product.id}?period=realtime`
            return (
              <tr key={`${product.level}:${product.id}`}>
                <td className="product-cell">
                  <Link to={detailUrl} className="product-link">
                    {displayImage
                      ? <img src={displayImage} alt="" loading="lazy" />
                      : <span className="image-fallback">无图</span>}
                    <span className="product-copy">
                      <strong title={record?.nameNote ? product.name : undefined}>{displayName}</strong>
                      <span>
                        {product.level === 'sku'
                          ? `京东SKU ${product.id} · 商家SKU ${merchantSku || '未映射'}`
                          : `SPU · ${product.id}`}
                      </span>
                      {!!badgesByProductId?.get(product.id)?.length && (
                        <span className="product-tag-row">
                          {badgesByProductId.get(product.id)?.map((badge) => (
                            <span key={badge.id} className={`tag-badge tag-${badge.tone}`} title={badge.title}>{badge.label}</span>
                          ))}
                        </span>
                      )}
                    </span>
                  </Link>
                </td>
                <td className="numeric">{money(product.metrics.gmv)}</td>
                <td className="numeric">{integer(product.metrics.soldItemCount)}</td>
                {erpAvailableByMerchantSku && (
                  <td className="numeric sku-list-erp-inventory" title={erpAvailable === undefined ? '未按商家SKU关联到ERP货品' : '网店管家ERP可订购数量'}>
                    {erpAvailable === undefined ? '—' : integer(erpAvailable)}
                  </td>
                )}
                {showAverageItemPrice && <td className="numeric">{money(product.metrics.averageItemPrice)}</td>}
                <td className="numeric">{integer(product.metrics.productVisitors)}</td>
                <td className="numeric">{integer(product.metrics.productPageviews)}</td>
                <td className="numeric">{percent(product.metrics.conversionRate)}</td>
                <td className="numeric">{money(product.metrics.averageOrderValue)}</td>
                <td className="numeric"><DeltaValue product={product} /></td>
                <td className="row-action">
                  <Link to={detailUrl} aria-label={`查看${product.name}实时详情`}><ChevronRight aria-hidden="true" /></Link>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {!products.length && <div className="empty-state">{emptyMessage}</div>}
    </div>
  )
}
